import {
  Box,
  Divider,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { DashboardFavorite } from 'lib-dashboard-types';
import { SearchQueue } from 'lib-search-types';
import { useHasAccess } from 'lib-shared-access-control';
import { isResponseWithData } from 'lib-shared-auth';
import {
  HookFormCheckbox,
  HookFormSelectionListSingle,
  HookFormText,
  HookFormTextarea,
} from 'lib-shared-hook-form';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMount } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { krakenApi } from 'apis/kraken';
import { usersApi } from 'apis/users';
import { HookFormAgentSelect, HookFormSearchSelect } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { sanitizeHtml } from 'utils/sanitizeHtml';

import { queueSortByPreferenceAssets } from '../../../../assets/dashboardPreferencesAssets';

import styles from './dashboardFavoriteModal.module.scss';
import { DashboardFavoriteModalType } from './dashboardFavoriteModal.types';

const formId = 'dashboard-favorite-form';

type FieldValues = Pick<
  DashboardFavorite,
  'name' | 'description' | 'shared' | 'params'
> & {
  /** The full queue objects corresponding to `params.queue_ids`. */
  readonly queues: readonly SearchQueue[];
  /**
   * The user that owns the favorite.
   *
   * @note Represented as an array to be compatible with the {@link HookFormAgentSelect} component.
   */
  user: User[];
};

/** A modal what for creating and updating dashboard favorites. */
export const DashboardFavoriteModal = createAppModal(
  { type: 'dashboard:favorite' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const { mode = 'create', favorite } = modal.arg;
    const hasAnalyticsAccess = useHasAccess('analytics');

    const [createFavorite, createState] =
      dashboardApi.useCreateDashboardFavoriteMutation();
    const [updateFavorite, updateState] =
      dashboardApi.useUpdateDashboardFavoriteMutation();

    const form = useForm<FieldValues>({
      mode: 'all',
      defaultValues: {
        name: favorite?.name ?? '',
        description: favorite?.description ?? '',
        shared: favorite?.shared ?? false,
        params: favorite?.params ?? modal.arg.params ?? { queue_ids: [] },
        queues: [],
        ...(favorite?.user && { user: [favorite.user] }),
      },
    });

    // Handles searching for the queues matching `params.queue_ids`, which is necessary in order to
    // display the currently selected queues in the queue selection list input field.
    useMount(() => {
      async function searchQueues() {
        const queueIds = form.watch('params.queue_ids');

        if (queueIds?.length) {
          const response = await dispatch(
            krakenApi.endpoints.readSearch.initiate({
              scope: 'queues',
              filters: {
                strategy: 'all',
                filters: [
                  {
                    field: 'id',
                    operator: 'is_one',
                    values: queueIds.map((id) => ({ value: id })),
                  },
                ],
              },
              pageSize: queueIds.length,
            }),
          );

          if (isResponseWithData(response)) {
            // The result from the Search API is not guaranteed to be in the same order as the
            // requested queue IDs, so we need to sort them here to match the original order.
            const results = response.data.data.results as ReadonlyArray<{
              result: SearchQueue;
            }>;
            const queues = queueIds.map(
              (id) => results.find((hit) => hit.result.id === id)!.result,
            );

            form.setValue('queues', queues ?? []);
          }
        }
      }

      searchQueues();
    });

    /** Handles creating a new dashboard favorite. */
    const handleCreateFavorite = async (values: FieldValues) => {
      if (mode !== 'create') return;

      try {
        const { queues, user, ...restValues } = values;

        const {
          data: { favorite: createdFavorite },
        } = await createFavorite({
          favorite: {
            dashboard: 'queue',
            ...restValues,
            params: {
              ...restValues.params,
              queue_ids: queues?.map((queue) => queue.id) ?? [],
            },
          },
        }).unwrap();

        ampli.dashboardFavoriteCreated({
          shared: values.shared,
          sortBy: values.params.sort_by,
        });

        ampli.dashboardFavoriteSharingToggled({
          shared: values.shared,
        });

        meta?.resolve({ favorite: createdFavorite });

        notify(
          {
            title: 'Favorite created',
            text: sanitizeHtml(
              `The favorite "${createdFavorite.name}" has been created.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 3000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while creating the favorite.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles updating an existing dashboard favorite. */
    const handleUpdateFavorite = async (values: FieldValues) => {
      if (!favorite) return;

      try {
        const { queues, user, ...restValues } = values;

        const {
          data: { favorite: updatedFavorite },
        } = await updateFavorite({
          id: favorite.id,
          favorite: {
            ...restValues,
            params: {
              ...restValues.params,
              queue_ids: queues?.map((queue) => queue.id) ?? [],
            },
          },
        }).unwrap();

        ampli.dashboardFavoriteUpdated({
          shared: values.shared,
          sortBy: values.params.sort_by,
        });

        if (favorite.shared !== updatedFavorite.shared) {
          ampli.dashboardFavoriteSharingToggled({
            shared: values.shared,
          });
        }

        meta?.resolve({ favorite: updatedFavorite });

        notify(
          {
            title: 'Favorite updated',
            text: sanitizeHtml(
              `The favorite "${updatedFavorite.name}" has been updated.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 3000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while updating the favorite.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles transferring ownership of an existing dashboard favorite. */
    const handleTransferOwnership = async (values: FieldValues) => {
      if (!favorite) return;

      try {
        const { queues, user, ...restValues } = values;

        const {
          data: { favorite: updatedFavorite },
        } = await updateFavorite({
          id: favorite.id,
          userId: user[0].id,
          favorite: {
            ...restValues,
            params: {
              ...restValues.params,
              queue_ids: queues?.map((queue) => queue.id) ?? [],
            },
          },
        }).unwrap();

        // Track only if the owner has changed.
        if (favorite.user.id !== updatedFavorite.user.id) {
          ampli.dashboardFavoriteOwnershipTransferred();
        }

        meta?.resolve({ favorite: updatedFavorite });

        notify(
          {
            title: 'Favorite ownership transferred',
            text: sanitizeHtml(
              `Ownership of "${values.name}" has been transferred.`,
              'plainText',
            ),
            icon: 'i-done',
            time: 3000,
          },
          NotificationType.Success,
        );

        hide?.();
      } catch (error) {
        notify(
          {
            title: 'Error while transferring ownership of the favorite.',
          },
          NotificationType.Error,
        );
      }
    };

    /** Handles valid form submit. */
    const handleValidSubmit: SubmitHandler<FieldValues> = (values) => {
      if (mode === 'create') {
        handleCreateFavorite(values);
      } else if (mode === 'update') {
        handleUpdateFavorite(values);
      } else if (mode === 'transferOwnership') {
        handleTransferOwnership(values);
      }
    };

    const handleCancel = (): void => {
      if (meta) {
        hide?.();

        meta.resolve({
          isCancelled: true,
        });
      }
    };

    /** Various texts according to the mode of the modal. */
    const texts: Record<
      DashboardFavoriteModalType,
      Record<'title' | 'button', string>
    > = {
      create: {
        title: 'Create favorite',
        button: 'Create favorite',
      },
      update: {
        title: 'Edit favorite',
        button: 'Save favorite',
      },
      transferOwnership: {
        title: 'Transfer favorite ownership',
        button: 'Transfer ownership',
      },
    };

    const fieldRules: Record<
      keyof FieldValues,
      Record<'visible' | 'disabled', boolean>
    > = {
      name: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      description: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      shared: {
        visible: hasAnalyticsAccess && mode !== 'transferOwnership',
        disabled: false,
      },
      params: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      queues: {
        visible: mode !== 'transferOwnership',
        disabled: false,
      },
      user: {
        visible: mode === 'transferOwnership',
        disabled: false,
      },
    };

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} className={styles.modal}>
        <ModalHead title={texts[mode].title} onClose={handleCancel} />

        <ModalBody>
          <form
            id={formId}
            noValidate
            onSubmit={form.handleSubmit(handleValidSubmit)}
          >
            <FormGrid>
              {fieldRules.name.visible && (
                <>
                  <FormField>
                    <Divider>
                      <Text.Paragraph as="span" color="tertiary">
                        Details
                      </Text.Paragraph>
                    </Divider>
                  </FormField>

                  <FormField>
                    <HookFormText
                      name="name"
                      label="Name"
                      control={form.control}
                      rules={{
                        required: 'Name is required',
                      }}
                      componentProps={{
                        autoFocus: true,
                      }}
                      disabled={fieldRules.name.disabled}
                    />
                  </FormField>
                </>
              )}

              {fieldRules.description.visible && (
                <FormField>
                  <HookFormTextarea
                    name="description"
                    label="Description"
                    control={form.control}
                    componentProps={{
                      height: 102,
                    }}
                    disabled={fieldRules.description.disabled}
                  />
                </FormField>
              )}

              {fieldRules.shared.visible && (
                <FormField>
                  <Box margin="0 0 300">
                    <HookFormCheckbox
                      name="shared"
                      label="Share this favorite with all users"
                      control={form.control}
                      helpIcon={{
                        tooltipClassName: styles.helpTooltip,
                        text: (
                          <Text.Paragraph variant="2" color="inherit">
                            Select the checkbox to make the favorite available
                            to all with full access to Analytics. You can always
                            change this again later.
                          </Text.Paragraph>
                        ),
                      }}
                      disabled={fieldRules.shared.disabled}
                    />
                  </Box>
                </FormField>
              )}

              {fieldRules.queues.visible && (
                <>
                  <FormField>
                    <Divider>
                      <Text.Paragraph as="span" color="tertiary">
                        Queues & sorting
                      </Text.Paragraph>
                    </Divider>
                  </FormField>

                  <FormField>
                    <HookFormSearchSelect
                      name="queues"
                      label="Queues"
                      control={form.control}
                      rules={{
                        required: 'Select at least one queue',
                      }}
                      componentProps={{
                        multiple: true,
                        placeholder: 'Select one or more queues',
                        equalityFn: (a, b) => a.id === b.id,
                        getItemTitleByValue: (queue) =>
                          queue?.name ?? queue?.id ?? '',
                      }}
                      scope="queues"
                      initialArg={{
                        sortBy: { field: 'name', order: 'asc' },
                      }}
                      transformValue={(queue) => queue}
                      renderSelectionListItemContent={(queue) => queue.name}
                      disabled={fieldRules.queues.disabled}
                    />
                  </FormField>

                  <FormField>
                    <HookFormSelectionListSingle
                      name="params.sort_by"
                      label="Sort by"
                      control={form.control}
                      componentProps={{
                        clearable: true,
                        placeholder: 'Custom',
                        getItemTitleByValue: (value) =>
                          queueSortByPreferenceAssets.find(
                            (asset) => asset.value === value,
                          )?.label ?? '',
                      }}
                      helpIcon={{
                        tooltipClassName: styles.helpTooltip,
                        text: (
                          <Text.Paragraph variant="2" color="inherit">
                            Choose how the queues are sorted in this favorite
                            list. If no sorting is chosen, the order of the
                            queues as selected in the field above will be used
                            when viewing this favorite list.
                          </Text.Paragraph>
                        ),
                      }}
                      disabled={fieldRules.params.disabled}
                    >
                      {queueSortByPreferenceAssets.map(
                        ({ value, label, image }) => (
                          <SelectionList.Item
                            key={value}
                            value={value}
                            contentClassName={styles.sortByItem}
                          >
                            {label} {image}
                          </SelectionList.Item>
                        ),
                      )}
                    </HookFormSelectionListSingle>
                  </FormField>
                </>
              )}

              {fieldRules.user.visible && (
                <FormField>
                  <HookFormAgentSelect
                    name="user"
                    label="Owner"
                    control={form.control}
                    helpIcon={{
                      tooltipClassName: styles.helpTooltip,
                      text: (
                        <Text.Paragraph variant="2" color="inherit">
                          The owner of the favorite can edit and delete it, or
                          transfer ownership to another user. Only users with
                          full access to Analytics can be owners.
                        </Text.Paragraph>
                      ),
                    }}
                    rules={{
                      required: 'Owner is required',
                      validate: {
                        hasAccess: async (user) => {
                          const userId = user[0].id;

                          // Skip validation if the selected user is the current owner.
                          if (userId === favorite?.user.id) return true;

                          try {
                            const response = await dispatch(
                              usersApi.endpoints.readUserRoles.initiate({
                                userId,
                              }),
                            ).unwrap();

                            const hasAnalyticsScope =
                              response.data.roles_with_scopes.some(
                                ({ role_scopes }) =>
                                  role_scopes.some(
                                    ({ scope }) => scope.value === 'analytics',
                                  ),
                              );

                            return (
                              hasAnalyticsScope ||
                              'The selected user does not have full access to Analytics.'
                            );
                          } catch (error) {
                            return 'Could not validate the selected user.';
                          }
                        },
                      },
                    }}
                    componentProps={{
                      lazy: true,
                    }}
                    disabled={fieldRules.user.disabled}
                  />
                </FormField>
              )}
            </FormGrid>
          </form>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleCancel}
          confirmName={texts[mode].button}
          confirmOnClick={noop}
          confirmProps={{
            type: 'submit',
            isLoading: createState.isLoading || updateState.isLoading,
            disabled: !form.formState.isValid || form.formState.isSubmitting,
            form: formId,
          }}
        />
      </Modal>
    );
  },
);
